/**
 * Migration Page
 *
 * WPML to MPZ Migration Wizard
 *
 * @package MultilingualPressZone
 * @since 3.0.0
 */

import { el, __, clear, formatNumber, formatDate } from '../utils/dom.js';
import api from '../utils/api.js';
import logger from '../utils/logger.js';
import Card from '../components/Card.js';
import { Button } from '../components/Button.js';
import ProgressBar from '../components/ProgressBar.js';
import { Spinner } from '../components/Spinner.js';
import { PageHeader } from '../components/PageHeader.js';

export default class MigrationPage {
    constructor(container) {
        this.container = container;
        this.step = 1;
        this.loading = false;
        this.error = null;
        this.checks = null;
        this.backup = null;
        this.migrationStatus = null;
        this.pollingInterval = null;
    }

    async render() {
        clear(this.container);
        this.container.className = 'mpz-migration-page';

        this.container.appendChild(
            PageHeader({
                title: __('WPML Migration Wizard', 'multilingual-press-zone'),
                description: __('Migrate your content from WPML to Multilingual Press Zone seamlessly.', 'multilingual-press-zone')
            })
        );

        // Step Indicator
        this.renderStepIndicator();

        // Main Content Area
        this.contentArea = el('div', { className: 'mpz-migration-content' });
        this.container.appendChild(this.contentArea);

        this.renderCurrentStep();
    }

    renderStepIndicator() {
        const steps = [
            __('Pre-flight Check', 'multilingual-press-zone'),
            __('Backup', 'multilingual-press-zone'),
            __('Migration', 'multilingual-press-zone'),
            __('Verification', 'multilingual-press-zone'),
            __('Complete', 'multilingual-press-zone')
        ];

        const indicator = el(
            'div',
            { className: 'mpz-step-indicator' },
            ...steps.map((label, index) => {
                const stepNum = index + 1;
                let className = 'mpz-step';
                if (stepNum === this.step) className += ' active';
                if (stepNum < this.step) className += ' completed';

                return el(
                    'div',
                    { className },
                    el('div', { className: 'mpz-step-number' }, stepNum.toString()),
                    el('div', { className: 'mpz-step-label' }, label)
                );
            })
        );
        this.container.appendChild(indicator);
    }

    renderCurrentStep() {
        clear(this.contentArea);

        if (this.error) {
            this.contentArea.appendChild(
                el(
                    'div',
                    { className: 'notice notice-error inline' },
                    el('p', {}, this.error)
                )
            );
        }

        switch (this.step) {
            case 1:
                this.renderStep1(); // Pre-flight
                break;
            case 2:
                this.renderStep2(); // Backup
                break;
            case 3:
                this.renderStep3(); // Migration
                break;
            case 4:
                this.renderStep4(); // Verification
                break;
            case 5:
                this.renderStep5(); // Complete
                break;
        }
    }

    // Step 1: Pre-flight Check
    async renderStep1() {
        if (!this.checks) {
            this.loading = true;
            this.renderLoading();
            try {
                const response = await api.get('/mpz/v1/migration/preflight');
                this.checks = response.checks;
                this.canProceed = response.can_proceed;
            } catch (err) {
                this.error = err.message;
            } finally {
                this.loading = false;
                this.renderCurrentStep(); // Re-render with data
            }
            return;
        }

        const title = __('System Check', 'multilingual-press-zone');
        const checkList = el('ul', { className: 'mpz-check-list' });

        Object.values(this.checks).forEach(check => {
            const icon = check.passed ? 'dashicons-yes-alt' : (check.severity === 'critical' ? 'dashicons-dismiss' : 'dashicons-warning');
            const iconVariant = check.passed ? 'success' : (check.severity === 'critical' ? 'error' : 'warning');

            checkList.appendChild(
                el(
                    'li',
                    { className: `mpz-check-item ${check.passed ? 'passed' : 'failed'}` },
                    el('span', { className: `dashicons ${icon} mpz-check-icon mpz-check-icon--${iconVariant}` }),
                    el('span', { className: 'mpz-check-message' }, check.message)
                )
            );
        });

        this.contentArea.appendChild(
            Card('✅', title, '', [checkList])
        );

        const actions = el(
            'div',
            { className: 'mpz-actions' },
            Button({
                label: __('Proceed to Backup', 'multilingual-press-zone'),
                variant: 'primary',
                disabled: !this.canProceed,
                onClick: () => {
                    this.step = 2;
                    this.render();
                }
            })
        );
        this.contentArea.appendChild(actions);
    }

    // Step 2: Backup
    async renderStep2() {
        const title = __('Database Backup', 'multilingual-press-zone');

        let content;
        if (this.backup) {
            content = el(
                'div',
                {},
                el(
                    'div',
                    { className: 'notice notice-success inline' },
                    el('p', {}, this.backup.message)
                ),
                el('p', {}, __('Backup saved successfully. You can strictly proceed with migration now.', 'multilingual-press-zone'))
            );
        } else if (this.loading) {
            content = el(
                'div',
                { className: 'presszone-multilingual-loading' },
                Spinner({ size: 'md', label: __('Creating backup... This may take a few minutes.', 'multilingual-press-zone') }),
                el('p', {}, __('Creating backup... This may take a few minutes.', 'multilingual-press-zone'))
            );
        } else {
            content = el(
                'div',
                {},
                el('p', {}, __('We strongly recommend creating a database backup before starting the migration.', 'multilingual-press-zone')),
                el('p', {}, __('This process will copy all WPML data (languages, translations, string translations) into Multilingual Press Zone tables.', 'multilingual-press-zone'))
            );
        }

        this.contentArea.appendChild(
            Card('💾', title, '', [content])
        );

        const actions = el('div', { className: 'mpz-actions' });

        if (!this.loading) {
            if (!this.backup) {
                actions.appendChild(
                    Button({
                        label: __('Create Backup', 'multilingual-press-zone'),
                        variant: 'primary',
                        onClick: () => this.runBackup()
                    })
                );

                // Allow skipping backup (use with caution)
                actions.appendChild(
                    Button({
                        label: __('Skip Backup (Not Recommended)', 'multilingual-press-zone'),
                        variant: 'ghost',
                        onClick: () => {
                            if (confirm(__('Are you sure you want to skip backup? Any data loss cannot be undone.', 'multilingual-press-zone'))) {
                                this.step = 3;
                                this.render();
                            }
                        }
                    })
                );
            } else {
                actions.appendChild(
                    Button({
                        label: __('Start Migration', 'multilingual-press-zone'),
                        variant: 'primary',
                        onClick: () => {
                            this.step = 3;
                            this.render();
                        }
                    })
                );
            }
        }

        this.contentArea.appendChild(actions);
    }

    async runBackup() {
        this.loading = true;
        this.renderCurrentStep();
        try {
            const response = await api.post('/mpz/v1/migration/backup');
            this.backup = response;
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
            this.renderCurrentStep();
        }
    }

    // Step 3: Migration Process
    renderStep3() {
        const title = __('Migration in Progress', 'multilingual-press-zone');

        // Initial state logic
        if (!this.migrationStatus && !this.loading) {
            // Start migration automatically
            this.startMigration();
        }

        const body = el('div', { className: 'mpz-migration-progress' });

        new ProgressBar({
            container: body,
            value: this.migrationStatus ? (this.migrationStatus.progress || 0) : 0,
            max: 100,
            label: this.migrationStatus
                ? `${__('Stage:', 'multilingual-press-zone')} ${this.migrationStatus.stage} - ${formatNumber(this.migrationStatus.processed_items)} ${__('items processed', 'multilingual-press-zone')}`
                : __('Initializing...', 'multilingual-press-zone'),
            status: this.migrationStatus && this.migrationStatus.completed ? 'completed' : 'in-progress',
        });

        this.contentArea.appendChild(
            Card('🚚', title, '', [body])
        );

        // Navigation (Success/Failure) depends on status
        if (this.migrationStatus && this.migrationStatus.completed) {
            const actions = el(
                'div',
                { className: 'mpz-actions' },
                Button({
                    label: __('Proceed to Verification', 'multilingual-press-zone'),
                    variant: 'primary',
                    onClick: () => {
                        this.step = 4;
                        this.render();
                    }
                })
            );
            this.contentArea.appendChild(actions);
        }
    }

    // Step 4: Verification
    async renderStep4() {
        const title = __('Verification', 'multilingual-press-zone');

        if (!this.verification && !this.loading) {
            this.loading = true;
            this.renderLoading();
            try {
                this.verification = await api.get('/mpz/v1/migration/verify');
            } catch (err) {
                this.error = err.message;
            } finally {
                this.loading = false;
                this.renderCurrentStep();
            }
            return;
        }

        const result = this.verification || {};
        const success = !!result.success;
        const message = result.message || (success
            ? __('Verification passed.', 'multilingual-press-zone')
            : __('Verification failed.', 'multilingual-press-zone'));

        const content = el(
            'div',
            {},
            el(
                'div',
                { className: `notice ${success ? 'notice-success' : 'notice-error'} inline` },
                el('p', {}, message)
            )
        );

        this.contentArea.appendChild(
            Card('🔎', title, '', [content])
        );

        const actions = el(
            'div',
            { className: 'mpz-actions' },
            Button({
                label: __('Finish', 'multilingual-press-zone'),
                variant: 'primary',
                onClick: () => {
                    this.step = 5;
                    this.render();
                }
            })
        );

        this.contentArea.appendChild(actions);
    }

    async startMigration() {
        this.loading = true;
        this.renderCurrentStep(); // Show loader/progress

        try {
            await api.post('/mpz/v1/migration/start', { dry_run: false }); // Start
            // Start polling
            this.pollingInterval = setInterval(() => this.processBatch(), 2000); // 2s polling
        } catch (err) {
            this.error = err.message;
            this.loading = false;
            this.renderCurrentStep();
        }
    }

    async processBatch() {
        try {
            const response = await api.post('/mpz/v1/migration/process');
            this.migrationStatus = response; // { success, message, stage, progress, completed }

            if (response.completed) {
                clearInterval(this.pollingInterval);
                this.loading = false;
            }

            this.renderCurrentStep(); // Update progress bar

        } catch (err) {
            clearInterval(this.pollingInterval);
            this.loading = false;
            this.renderCurrentStep();
        }
    }

    // Step 5: Complete
    renderStep5() {
        const title = __('Complete', 'multilingual-press-zone');
        const content = el(
            'div',
            { className: 'mpz-success-message' },
            el('span', { className: 'dashicons dashicons-yes-alt mpz-success-icon', 'aria-hidden': 'true' }),
            el('h2', {}, __('Migration Successful!', 'multilingual-press-zone')),
            el('p', {}, __('Your WPML data has been successfully migrated to Multilingual Press Zone.', 'multilingual-press-zone'))
        );

        this.contentArea.appendChild(
            Card('🎉', title, '', [content])
        );

        // Cleanup polling just in case
        if (this.pollingInterval) clearInterval(this.pollingInterval);
    }

    renderLoading() {
        clear(this.contentArea);
        this.contentArea.appendChild(
            el(
                'div',
                { className: 'presszone-multilingual-loading' },
                Spinner({ size: 'md', label: __('Loading...', 'multilingual-press-zone') }),
                el('p', {}, __('Loading...', 'multilingual-press-zone'))
            )
        );
    }
}
